import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../gyzmo-commons/dao/db/base/db.dao.base";
import { AppSqlProvider } from "../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";
import { HourType } from "../../models/hourType";

@Injectable({
    providedIn: "root",
})
export class HourTypeDbDao extends DbDaoBase<HourType> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider) {
        super(logger);
    }

    public async getList(hydrate: boolean = false) {
        let selectQuery = "SELECT * FROM " + HourType.TABLENAME + ";";

        return this.listByQuery(selectQuery);
    }

    public list(kindFilter: string): Promise<HourType[]> {
        let selectQuery = "SELECT * FROM " + HourType.TABLENAME + " WHERE hourType='" + kindFilter + "';";

        return this.listByQuery(selectQuery);
    }

    public async createIndexes(): Promise<void> {
        let query = "CREATE INDEX IF NOT EXISTS idx_" + HourType.TABLENAME + "_id"
                    + " ON " + HourType.TABLENAME + "(id);";

        await this.sqlProvider.query(query)
            .catch(reason => {
                this.logSqlError(reason);
            });

        query = "CREATE INDEX IF NOT EXISTS idx_" + HourType.TABLENAME + "_hourType"
                + " ON " + HourType.TABLENAME + "(hourType);";

        await this.sqlProvider.query(query)
            .catch(reason => {
                this.logSqlError(reason);
            });
    }

    public createTable(): Promise<void> {
        let query = "CREATE TABLE IF NOT EXISTS " + HourType.TABLENAME
                    + " ("
                    + "id TEXT PRIMARY KEY, "
                    + "hourType TEXT, "
                    + "wording TEXT "
                    + ");";

        return this.sqlProvider.query(query)
            .then(async () => {
                await this.createIndexes();
                return;
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        let selectQuery = "DELETE FROM " + HourType.TABLENAME + " WHERE id='" + id + "';";
        return this.sqlProvider.query(selectQuery);
    }

    deleteAll(): Promise<any> {
        let selectQuery = "DELETE FROM " + HourType.TABLENAME + ";";
        return this.sqlProvider.query(selectQuery);
    }

    public get(id: string, hydrate: boolean = false): Promise<HourType> {
        let selectQuery = "SELECT * FROM " + HourType.TABLENAME + " WHERE id = '" + id + "';";

        return this.sqlProvider.query(selectQuery)
            .then(
                data => {
                    if (data.rows.length <= 0) {
                        return null;
                    }

                    let returnCheckStatus = this.rowToModel(data.rows[0]);

                    let hydratationPromises = [];

                    if (hydrate) {
                        // Nothing
                    }

                    return Promise.all(hydratationPromises)
                        .then(ignored => {
                            return returnCheckStatus;
                        });
                })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return HourType.TABLENAME;
    }

    protected rowToModel(row: any): HourType {
        let hourType = new HourType();

        hourType.id = row.id;
        hourType.hourType = row.hourType;
        hourType.wording = row.wording;

        return hourType;
    }

    public save(returnCheckStatus: HourType): Promise<HourType> {
        let promises = [];

        return Promise.all(promises)
            .then(value => {
                let query = "INSERT OR REPLACE INTO " + HourType.TABLENAME + " ("
                            + "id, "
                            + "hourType, "
                            + "wording "
                            + ") VALUES ("
                            + this.getValue(returnCheckStatus.id)
                            + this.getValue(returnCheckStatus.hourType)
                            + this.getValue(returnCheckStatus.wording, true)
                            + ");";

                return this.sqlProvider.query(query)
                    .then(response => {
                        return returnCheckStatus;
                    })
                    .catch(reason => {
                        this.logSqlError(reason);
                        return null;
                    });
            });
    }

    private listByQuery(query: string): Promise<HourType[]> {
        return this.sqlProvider.query(query)
            .then(data => {
                if (data.rows.length <= 0) {
                    return [];
                }

                let returnHourTypes: HourType[] = [];
                for (let i = 0; i < data.rows.length; i++) {
                    returnHourTypes.push(this.rowToModel(data.rows[i]));
                }

                let hydratationPromises = [];
                return Promise.all(hydratationPromises)
                    .then(ignored => {
                        return returnHourTypes;
                    });
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }
}
